import React, { useRef } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  AddPhotoAlternateOutlined as AddPhotoIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { MAX_PHOTOS } from '../../utils/media';

/**
 * Picking the photos of a post or an event, with what `usePhotoUploads`
 * returns. Each thumbnail shows its own state — sending, sent, refused — so a
 * photo that fails does not hold the others back.
 */
function PhotoPicker({ uploads, disabled = false }) {
  const input = useRef(null);
  const { photos, add, remove, full } = uploads;

  const pick = (event) => {
    add(event.target.files);
    // Picking the same file again after removing it must fire a change.
    event.target.value = '';
  };

  return (
    <Stack spacing={1.25}>
      {photos.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          {photos.map((photo, index) => (
            <Box
              key={photo.key}
              sx={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '10px',
                overflow: 'hidden',
                bgcolor: 'surface.chip',
                outline: 'error' === photo.status ? '2px solid' : 'none',
                outlineColor: 'error.main',
              }}
            >
              {photo.preview && (
                <Box
                  component="img"
                  src={photo.preview}
                  alt={`Photo ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: 'done' === photo.status ? 1 : 0.5,
                  }}
                />
              )}

              {'uploading' === photo.status && (
                <CircularProgress
                  size={24}
                  aria-label="Envoi en cours"
                  sx={{ position: 'absolute', top: 'calc(50% - 12px)', left: 'calc(50% - 12px)' }}
                />
              )}

              {'error' === photo.status && (
                <Tooltip title={photo.error}>
                  <ErrorIcon
                    color="error"
                    aria-label={photo.error}
                    sx={{ position: 'absolute', top: 'calc(50% - 12px)', left: 'calc(50% - 12px)' }}
                  />
                </Tooltip>
              )}

              <IconButton
                size="small"
                aria-label={`Retirer la photo ${index + 1}`}
                onClick={() => remove(photo.key)}
                disabled={disabled}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(0, 0, 0, 0.55)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {photos
        .filter((photo) => 'error' === photo.status)
        .map((photo) => (
          <Typography key={photo.key} variant="caption" color="error" role="alert">
            {photo.error}
          </Typography>
        ))}

      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<AddPhotoIcon />}
          onClick={() => input.current?.click()}
          disabled={disabled || full}
        >
          Ajouter des photos
        </Button>
        <Typography variant="caption">
          {photos.length}/{MAX_PHOTOS} · JPEG, PNG, WebP ou HEIC
        </Typography>
      </Stack>

      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        hidden
        onChange={pick}
        data-testid="photo-input"
      />
    </Stack>
  );
}

export default PhotoPicker;
